import { useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  ImageIcon, 
  Star, 
  X, 
  Plus, 
  Grid3X3,
  LayoutGrid,
  Maximize2,
  ChevronUp,
  ChevronDown,
  Play,
  Sparkles
} from "lucide-react";
import { Button } from "@/components/ui/button";

// Collage layout options
const collageLayouts = [
  { 
    id: "open_house", 
    name: "Hero Focus", 
    desc: "Hero + Title + Thumbnails + Agent Info",
    icon: LayoutGrid,
    preview: "grid-cols-2 grid-rows-2"
  },
  { 
    id: "grid_3x3", 
    name: "Grid 3x3", 
    desc: "Hero takes 2x2, thumbnails fill rest",
    icon: Grid3X3,
    preview: "grid-cols-3 grid-rows-3"
  },
  { 
    id: "hero_top", 
    name: "Hero Top", 
    desc: "Hero on top (60%), details + thumbnails below",
    icon: Maximize2,
    preview: "grid-cols-3 grid-rows-2"
  },
  { 
    id: "hero_left", 
    name: "Hero Left", 
    desc: "Hero on left (60%), details on right",
    icon: LayoutGrid,
    preview: "grid-cols-2 grid-rows-2"
  }
];

// Video transition options - matches backend /api/content/video/transitions
const transitionOptions = [
  { id: "none", name: "No Transition", desc: "Static display" },
  { id: "fade", name: "Fade", desc: "Smooth crossfade" },
  { id: "dissolve", name: "Dissolve", desc: "Gradual dissolve" },
  { id: "fly_in", name: "Fly In", desc: "Slide in from right" },
  { id: "fly_out", name: "Fly Out", desc: "Slide out to left" },
  { id: "slide", name: "Slide", desc: "Push transition" },
  { id: "zoom", name: "Zoom", desc: "Zoom transition" },
  { id: "wipe", name: "Wipe", desc: "Horizontal wipe" },
  { id: "ken_burns", name: "Ken Burns", desc: "Documentary pan/zoom" }
];

export default function MultiImageCollage({
  images = [],
  onImagesChange,
  heroIndex = 0,
  onHeroChange,
  layout = "open_house",
  onLayoutChange,
  transition = "fade",
  onTransitionChange,
  isVideoOutput = false,
  expanded = true,
  onExpandedChange
}) {
  const [isDragging, setIsDragging] = useState(false);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  
  // Handle file upload
  const handleFileUpload = useCallback((e) => {
    const files = Array.from(e.target.files || []);
    const validImages = files.filter(f => f.type.startsWith("image/")).slice(0, 9 - images.length);
    if (validImages.length > 0) {
      onImagesChange([...images, ...validImages]);
    }
  }, [images, onImagesChange]);
  
  // Remove image
  const removeImage = useCallback((index) => {
    const newImages = images.filter((_, i) => i !== index);
    onImagesChange(newImages);
    // Adjust hero index if needed
    if (heroIndex >= newImages.length) {
      onHeroChange(Math.max(0, newImages.length - 1));
    } else if (index < heroIndex) {
      onHeroChange(heroIndex - 1);
    }
  }, [images, heroIndex, onImagesChange, onHeroChange]);
  
  // Set as hero
  const setAsHero = useCallback((index) => {
    onHeroChange(index);
  }, [onHeroChange]);
  
  // Drag and drop reordering
  const handleDragStart = (e, index) => {
    e.dataTransfer.setData("text/plain", index.toString());
    setIsDragging(true);
  };
  
  const handleDragOver = (e, index) => {
    e.preventDefault();
    setDragOverIndex(index);
  };
  
  const handleDrop = (e, targetIndex) => {
    e.preventDefault();
    const sourceIndex = parseInt(e.dataTransfer.getData("text/plain"));
    if (sourceIndex !== targetIndex) {
      const newImages = [...images];
      const [removed] = newImages.splice(sourceIndex, 1);
      newImages.splice(targetIndex, 0, removed);
      onImagesChange(newImages);
      
      // Update hero index if it was moved
      if (heroIndex === sourceIndex) {
        onHeroChange(targetIndex);
      } else if (sourceIndex < heroIndex && targetIndex >= heroIndex) {
        onHeroChange(heroIndex - 1);
      } else if (sourceIndex > heroIndex && targetIndex <= heroIndex) {
        onHeroChange(heroIndex + 1);
      }
    }
    setIsDragging(false);
    setDragOverIndex(null);
  };
  
  const handleDragEnd = () => {
    setIsDragging(false);
    setDragOverIndex(null);
  };
  
  if (images.length === 0) {
    return null; // Don't show until images are uploaded
  }
  
  return (
    <div className="space-y-4">
      {/* Collapsible Header */}
      <button
        onClick={() => onExpandedChange && onExpandedChange(!expanded)}
        className="w-full flex items-center justify-between p-3 rounded-lg bg-purple-500/10 border border-purple-500/30 hover:bg-purple-500/20 transition-colors"
      >
        <div className="flex items-center gap-3">
          <Grid3X3 className="w-5 h-5 text-purple-400" />
          <div className="text-left">
            <p className="font-medium text-purple-300">Multi-Image Collage</p>
            <p className="text-xs text-viraly-muted">
              {images.length} images • {collageLayouts.find(l => l.id === layout)?.name || "Open House"} layout
            </p>
          </div>
        </div>
        {expanded ? (
          <ChevronUp className="w-5 h-5 text-purple-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-purple-400" />
        )}
      </button>
      
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="space-y-4 overflow-hidden"
          >
            {/* Image Grid */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-viraly-light">
                  Images ({images.length}/9)
                </p>
                <p className="text-xs text-viraly-muted">
                  Drag to reorder • Click star to set Hero
                </p>
              </div>
              
              <div className="grid grid-cols-3 gap-2">
                {images.map((image, index) => (
                  <div
                    key={index}
                    draggable
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDrop={(e) => handleDrop(e, index)}
                    onDragEnd={handleDragEnd}
                    className={`relative aspect-square rounded-lg overflow-hidden cursor-grab active:cursor-grabbing border-2 transition-all ${
                      heroIndex === index 
                        ? "border-yellow-400 ring-2 ring-yellow-400/30" 
                        : dragOverIndex === index
                          ? "border-purple-400"
                          : "border-white/10 hover:border-white/30"
                    }`}
                  >
                    <img
                      src={URL.createObjectURL(image)}
                      alt={`Image ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                    
                    {/* Hero badge */}
                    {heroIndex === index && (
                      <div className="absolute top-1 left-1 px-2 py-0.5 bg-yellow-500 rounded text-[10px] font-bold text-black">
                        HERO
                      </div>
                    )}
                    
                    {/* Index badge */}
                    <div className="absolute bottom-1 left-1 w-5 h-5 rounded bg-black/70 flex items-center justify-center text-[10px] text-white">
                      {index + 1}
                    </div>
                    
                    {/* Action buttons */}
                    <div className="absolute top-1 right-1 flex gap-1">
                      {heroIndex !== index && (
                        <button
                          onClick={() => setAsHero(index)}
                          className="w-6 h-6 rounded bg-black/70 flex items-center justify-center hover:bg-yellow-500 transition-colors"
                          title="Set as Hero Image"
                        >
                          <Star className="w-3 h-3 text-white" />
                        </button>
                      )}
                      <button
                        onClick={() => removeImage(index)}
                        className="w-6 h-6 rounded bg-black/70 flex items-center justify-center hover:bg-red-500 transition-colors"
                        title="Remove Image"
                      >
                        <X className="w-3 h-3 text-white" />
                      </button>
                    </div>
                  </div>
                ))}
                
                {/* Add more button */}
                {images.length < 9 && (
                  <label className="aspect-square rounded-lg border-2 border-dashed border-white/20 flex flex-col items-center justify-center cursor-pointer hover:border-purple-400 hover:bg-purple-500/10 transition-all">
                    <Plus className="w-6 h-6 text-viraly-muted" />
                    <span className="text-[10px] text-viraly-muted mt-1">Add</span>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      className="hidden"
                      onChange={handleFileUpload}
                    />
                  </label>
                )}
              </div>
            </div>
            
            {/* Layout Selection */}
            <div className="space-y-2">
              <p className="text-sm font-medium text-viraly-light">Layout Style</p>
              <div className="grid grid-cols-2 gap-2">
                {collageLayouts.map((layoutOpt) => {
                  const Icon = layoutOpt.icon;
                  return (
                    <button
                      key={layoutOpt.id}
                      onClick={() => onLayoutChange(layoutOpt.id)}
                      className={`p-3 rounded-lg text-left transition-all ${
                        layout === layoutOpt.id
                          ? "bg-purple-500/20 border-2 border-purple-500"
                          : "bg-white/5 border-2 border-white/10 hover:border-white/20"
                      }`}
                      data-testid={`layout-${layoutOpt.id}`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <Icon className={`w-4 h-4 ${layout === layoutOpt.id ? "text-purple-400" : "text-viraly-muted"}`} />
                        <span className="text-sm font-medium">{layoutOpt.name}</span>
                      </div>
                      <p className="text-[10px] text-viraly-muted">{layoutOpt.desc}</p>
                    </button>
                  );
                })}
              </div>
            </div>
            
            {/* Video Transitions (only show for video output) */}
            {isVideoOutput && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Play className="w-4 h-4 text-purple-400" />
                  <p className="text-sm font-medium text-viraly-light">Image Transitions</p>
                  <span className="text-xs px-2 py-0.5 bg-purple-500/20 text-purple-400 rounded">Video</span>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {transitionOptions.map((trans) => (
                    <button
                      key={trans.id}
                      onClick={() => onTransitionChange(trans.id)}
                      className={`p-2 rounded-lg text-center transition-all ${
                        transition === trans.id
                          ? "bg-purple-500/20 border-2 border-purple-500"
                          : "bg-white/5 border-2 border-white/10 hover:border-white/20"
                      }`}
                      data-testid={`transition-${trans.id}`}
                    >
                      <p className="text-xs font-medium">{trans.name}</p>
                      <p className="text-[9px] text-viraly-muted">{trans.desc}</p>
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            {/* Layout Preview */}
            <div className="space-y-2">
              <p className="text-sm font-medium text-viraly-light flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-purple-400" />
                Preview
              </p>
              <div className="aspect-square max-w-[200px] mx-auto rounded-lg overflow-hidden bg-white/5 border border-white/10">
                {layout === "open_house" && (
                  <div className="w-full h-full grid grid-rows-[auto_1fr_auto] gap-1 p-1">
                    <div className="h-6 bg-white/20 rounded flex items-center px-2">
                      <span className="text-[8px] text-white/70">OPEN HOUSE • Sat 2-4PM</span>
                    </div>
                    <div className="bg-purple-500/30 rounded relative">
                      {images[heroIndex] && (
                        <img src={URL.createObjectURL(images[heroIndex])} className="w-full h-full object-cover rounded" alt="Hero" />
                      )}
                    </div>
                    <div className="flex gap-1">
                      {images.slice(0, 4).filter((_, i) => i !== heroIndex).slice(0, 3).map((img, i) => (
                        <div key={i} className="flex-1 h-8 bg-white/10 rounded overflow-hidden">
                          <img src={URL.createObjectURL(img)} className="w-full h-full object-cover" alt="" />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {layout === "grid_3x3" && (
                  <div className="w-full h-full grid grid-cols-3 grid-rows-3 gap-0.5 p-0.5">
                    <div className="col-span-2 row-span-2 bg-purple-500/30 rounded overflow-hidden">
                      {images[heroIndex] && (
                        <img src={URL.createObjectURL(images[heroIndex])} className="w-full h-full object-cover" alt="Hero" />
                      )}
                    </div>
                    {images.filter((_, i) => i !== heroIndex).slice(0, 5).map((img, i) => (
                      <div key={i} className="bg-white/10 rounded overflow-hidden">
                        <img src={URL.createObjectURL(img)} className="w-full h-full object-cover" alt="" />
                      </div>
                    ))}
                  </div>
                )}
                {layout === "hero_top" && (
                  <div className="w-full h-full grid grid-rows-[3fr_1fr_1fr] gap-0.5 p-0.5">
                    <div className="bg-purple-500/30 rounded overflow-hidden">
                      {images[heroIndex] && (
                        <img src={URL.createObjectURL(images[heroIndex])} className="w-full h-full object-cover" alt="Hero" />
                      )}
                    </div>
                    <div className="bg-white/20 rounded flex items-center px-2">
                      <span className="text-[7px] text-white/70">123 Main St • $899K</span>
                    </div>
                    <div className="flex gap-0.5">
                      {images.filter((_, i) => i !== heroIndex).slice(0, 4).map((img, i) => (
                        <div key={i} className="flex-1 bg-white/10 rounded overflow-hidden">
                          <img src={URL.createObjectURL(img)} className="w-full h-full object-cover" alt="" />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {layout === "hero_left" && (
                  <div className="w-full h-full grid grid-cols-[3fr_2fr] gap-0.5 p-0.5">
                    <div className="bg-purple-500/30 rounded overflow-hidden">
                      {images[heroIndex] && (
                        <img src={URL.createObjectURL(images[heroIndex])} className="w-full h-full object-cover" alt="Hero" />
                      )}
                    </div>
                    <div className="flex flex-col gap-0.5">
                      <div className="h-6 bg-red-500/50 rounded flex items-center justify-center">
                        <span className="text-[7px] text-white font-bold">FOR SALE</span>
                      </div>
                      <div className="flex-1 bg-white/10 rounded p-1">
                        <span className="text-[6px] text-white/70">Details</span>
                      </div>
                      <div className="grid grid-cols-2 gap-0.5 flex-1">
                        {images.filter((_, i) => i !== heroIndex).slice(0, 4).map((img, i) => (
                          <div key={i} className="bg-white/10 rounded overflow-hidden">
                            <img src={URL.createObjectURL(img)} className="w-full h-full object-cover" alt="" />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
